import React, { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import * as yup from 'yup';

import { createColumnHelper } from '@tanstack/react-table';

import AdminFormPage from 'components/admin/shared/AdminFormPage';
import Button from 'components/shared/Button';
import ButtonGroup from 'components/shared/ButtonGroup';
import CircularProgress from 'components/shared/CircularProgress';
import DataTable from 'components/shared/DataTable';
import FormHeader from 'components/shared/form/FormHeader';
import { Select } from 'components/shared/form/InfiniteScrollDropdown';
import Input from 'components/shared/form/Input';
import InputGroup from 'components/shared/form/InputGroup';
import SectionHeader from 'components/shared/form/SectionHeader';
import StandardModal, { Actions, ColorThemeName, ModalContent } from 'components/shared/StandardModal';
import { STATE_OPTIONS } from 'constants/states';
import { TIMEZONES } from 'constants/timezones';
import { useProfile } from 'context/profile';
import { getFieldError, useAdminForm } from 'hooks/datasource';
import useInfiniteOptions from 'hooks/useInfiniteOptions';
import { Location } from 'models/flexible/Location';
import { PERMISSIONS } from 'models/flexible/Profile';
import { adminIndexClients } from 'services/api/admin/clients';
import { archiveGroup } from 'services/api/admin/groups';
import { indexGroupTypes } from 'services/api/admin/groupTypes';
import {
  adminLocationsQueryKeys,
  adminShowLocation,
  createLocation,
  updateLocation,
} from 'services/api/admin/locations';
import type { AdminLocationShowFields } from 'services/api/fields/admin/location';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';
import TrashIcon from 'svg/TrashIcon';

type GroupRow = {
  id: string;
  name: string;
  groupType?: { id: string; displayName: string };
};

type GroupTypeRow = {
  id: string;
  displayName: string;
};

type TableRow =
  | { kind: 'group'; id: string; label: string; groupTypeName: string }
  | { kind: 'groupType'; id: string; label: string; groupTypeName: string };

const getId = (o: any) => o!.id as string;
const getName = (o: any) => o!.name as string;
const getDisplayName = (o: any) => o!.displayName as string;

const schema = yup.object({
  name: yup.string().required('Location name required'),
  client: yup.mixed().test('client', 'Client is required', (v: any) => !!v?.id),
  timezone: yup.mixed().test('timezone', 'Timezone is required', (v: any) => !!v?.value),
});

export default function EditLocation() {
  const { id } = useParams();
  const isEditing = !!id;
  const { profile } = useProfile();

  const form = useAdminForm({
    id,
    show: adminShowLocation,
    create: createLocation,
    update: updateLocation,
    include: ['groups'] as const,
    queryKey: adminLocationsQueryKeys,
    schema,
    toFormValues: (data: Location<AdminLocationShowFields<'groups'>> | undefined) => ({
      name: data?.name ?? '',
      externalId: data?.externalId ?? '',
      npiNumber: data?.npiNumber ?? '',
      client: data?.client?.id ? data.client : (null as { id: string; name: string } | null),
      timezone: data?.timezone
        ? (TIMEZONES.find((tz) => tz.value === data.timezone) ?? null)
        : (null as { label: string; value: string } | null),
      groups: (data?.groups ?? []) as GroupRow[],
      groupTypeIds: [] as string[],
      address: {
        address1: data?.address?.address1 ?? '',
        address2: data?.address?.address2 ?? '',
        city: data?.address?.city ?? '',
        state: data?.address?.state
          ? { label: data.address.state, value: data.address.state }
          : (null as { label: string; value: string } | null),
        zip: data?.address?.zip ?? '',
      },
    }),
    toPayload: (v) => ({
      name: v.name,
      externalId: v.externalId || null,
      npiNumber: v.npiNumber || null,
      clientId: v.client?.id,
      timezone: v.timezone?.value ?? '',
      groupTypeIds: v.groupTypeIds ?? [],
      address: {
        address1: v.address?.address1 || null,
        address2: v.address?.address2 || null,
        city: v.address?.city || null,
        state: v.address?.state?.value || null,
        zip: v.address?.zip || null,
      },
    }),
    onCreateSuccess: { toast: 'Location successfully added!', navigate: '/admin/locations' },
    onUpdateSuccess: { toast: 'Location successfully updated!', navigate: '/admin/locations' },
  });

  const canEdit = profile.has(PERMISSIONS.adminGroupEdit);
  const existingGroups = useMemo(() => form.values.groups ?? [], [form.values.groups]);
  const [addedGroupTypes, setAddedGroupTypes] = useState<GroupTypeRow[]>([]);

  const existingGroupTypeIds = useMemo(
    () => new Set(existingGroups.map((g) => g.groupType?.id).filter(Boolean)),
    [existingGroups]
  );

  const allUsedGroupTypeIds = useMemo(
    () => new Set([...existingGroupTypeIds, ...addedGroupTypes.map((gt) => gt.id)]),
    [existingGroupTypeIds, addedGroupTypes]
  );

  const tableRows: TableRow[] = useMemo(
    () => [
      ...existingGroups.map((g) => ({
        kind: 'group' as const,
        id: g.id,
        label: g.name,
        groupTypeName: g.groupType?.displayName || '',
      })),
      ...addedGroupTypes.map((gt) => ({
        kind: 'groupType' as const,
        id: gt.id,
        label: '',
        groupTypeName: gt.displayName,
      })),
    ],
    [existingGroups, addedGroupTypes]
  );

  const clientOpts = useInfiniteOptions({
    queryKey: ['adminClients', 'locations', { leaf: true }],
    fetchFn: adminIndexClients,
    params: { leaf: true, sortBy: 'name asc' },
  });

  const groupTypeOpts = useInfiniteOptions({
    queryKey: ['groupTypes', 'forLocation'],
    fetchFn: indexGroupTypes,
    params: { sortBy: 'displayName asc' },
  });

  const handleClientChange = (client: any) => {
    form.setValue('client', client);
    setAddedGroupTypes([]);
    form.setValue('groupTypeIds', []);
  };

  const handleAddGroupType = useCallback(
    (groupType: any) => {
      const row: GroupTypeRow = { id: groupType.id, displayName: groupType.displayName };
      const updated = [...addedGroupTypes, row];
      setAddedGroupTypes(updated);
      form.setValue('groupTypeIds', [...(form.values.groupTypeIds || []), groupType.id]);
    },
    [addedGroupTypes, form]
  );

  const handleRemoveGroupType = useCallback(
    (groupTypeId: string) => {
      setAddedGroupTypes(addedGroupTypes.filter((gt) => gt.id !== groupTypeId));
      form.setValue(
        'groupTypeIds',
        (form.values.groupTypeIds || []).filter((id: string) => id !== groupTypeId)
      );
    },
    [addedGroupTypes, form]
  );

  const [archiveTarget, setArchiveTarget] = useState<TableRow | null>(null);

  const handleArchiveGroup = useCallback(async () => {
    if (!archiveTarget) return;
    await archiveGroup(archiveTarget.id);
    form.setValue(
      'groups',
      (form.values.groups ?? []).filter((g: GroupRow) => g.id !== archiveTarget.id)
    );
    setArchiveTarget(null);
  }, [archiveTarget, form]);

  const columnHelper = createColumnHelper<TableRow>();
  const columns = useMemo(
    () => [
      ...(isEditing
        ? [
            columnHelper.accessor('label', {
              header: 'Group',
              cell: (data) => {
                const row = data.row.original;
                if (row.kind === 'group') {
                  return (
                    <GroupLink href={`/admin/groups/${row.id}/edit`} target='_blank' rel='noopener noreferrer'>
                      {data.getValue()}
                    </GroupLink>
                  );
                }
                return <BoldText>{data.getValue()}</BoldText>;
              },
              enableSorting: false,
            }),
          ]
        : []),
      columnHelper.accessor('groupTypeName', {
        header: 'Group Type',
        cell: (data) => data.getValue(),
        enableSorting: false,
      }),
      columnHelper.display({
        id: 'actions',
        header: '',
        cell: (data) => {
          const row = data.row.original;
          if (!canEdit) return null;
          if (row.kind === 'groupType') {
            return (
              <IconContainer onClick={() => handleRemoveGroupType(row.id)}>
                <TrashIcon style={{ color: colors.accentRed }} />
              </IconContainer>
            );
          }
          if (row.kind === 'group' && isEditing) {
            return (
              <IconContainer onClick={() => setArchiveTarget(row)}>
                <TrashIcon style={{ color: colors.accentRed }} />
              </IconContainer>
            );
          }
          return null;
        },
        size: 40,
      }),
    ],
    [handleRemoveGroupType, canEdit, columnHelper, isEditing]
  );

  if (isEditing && form.loading) return <CenteredSpinner />;

  return (
    <AdminFormPage>
      <>
        <FormHeader title={isEditing ? 'Edit Location' : 'Add Location'} />

        <SectionHeader>Location Information</SectionHeader>

        <InputGroup title='Client' error={getFieldError(form.errors, 'client')}>
          <Select
            {...clientOpts}
            value={form.values.client}
            onChange={handleClientChange}
            getOptionValue={getId}
            getOptionLabel={getName}
            placeholder='Client'
            isDisabled={!canEdit}
          />
        </InputGroup>

        <InputGroup title='Location Name' error={form.errors.name}>
          <Input
            value={form.values.name ?? ''}
            onChange={(e) => form.setValue('name', e.target.value)}
            placeholder='Location Name'
            $hasError={!!form.errors.name}
            disabled={!canEdit}
          />
        </InputGroup>

        <InputGroup title='External ID'>
          <Input
            value={form.values.externalId ?? ''}
            onChange={(e) => form.setValue('externalId', e.target.value)}
            placeholder='External ID'
            disabled={!canEdit}
          />
        </InputGroup>

        <InputGroup title='NPI Number'>
          <Input
            value={form.values.npiNumber ?? ''}
            onChange={(e) => form.setValue('npiNumber', e.target.value)}
            placeholder='NPI Number'
            disabled={!canEdit}
          />
        </InputGroup>

        <InputGroup title='Timezone'>
          <Select
            options={TIMEZONES}
            value={form.values.timezone}
            onChange={(v) => form.setValue('timezone', v)}
            getOptionValue={(o) => o.value}
            getOptionLabel={(o) => o.label}
            placeholder='Timezone'
            isDisabled={!canEdit}
          />
        </InputGroup>

        <SectionHeader>Group Types</SectionHeader>

        <InputGroup title='Add Group Type'>
          <Select
            {...groupTypeOpts}
            value={null}
            onChange={handleAddGroupType}
            getOptionValue={getId}
            getOptionLabel={getDisplayName}
            placeholder='Search for a group type...'
            isDisabled={!form.values.client?.id || !canEdit}
            filterOption={(option: any) => !allUsedGroupTypeIds.has(option.data?.id)}
          />
        </InputGroup>

        {tableRows.length > 0 && (
          <GroupsTableWrapper>
            <DataTable columns={columns} data={tableRows} />
          </GroupsTableWrapper>
        )}

        <SectionHeader>Address</SectionHeader>

        <InlineGroup>
          <InputGroup title='Street 1'>
            <Input
              value={form.values.address?.address1 ?? ''}
              onChange={(e) => form.setValue('address.address1', e.target.value)}
              placeholder='Street 1'
              disabled={!canEdit}
            />
          </InputGroup>
          <InputGroup title='Street 2 (optional)'>
            <Input
              value={form.values.address?.address2 ?? ''}
              onChange={(e) => form.setValue('address.address2', e.target.value)}
              placeholder='Street 2'
              disabled={!canEdit}
            />
          </InputGroup>
        </InlineGroup>

        <InlineGroup>
          <InputGroup title='City'>
            <Input
              value={form.values.address?.city ?? ''}
              onChange={(e) => form.setValue('address.city', e.target.value)}
              placeholder='City'
              disabled={!canEdit}
            />
          </InputGroup>
          <InlineGroup>
            <InputGroup title='State'>
              <Select
                options={STATE_OPTIONS}
                value={form.values.address?.state}
                onChange={(v) => form.setValue('address.state', v)}
                getOptionValue={(o) => o.value}
                getOptionLabel={(o) => o.label}
                placeholder='State'
                isDisabled={!canEdit}
              />
            </InputGroup>
            <InputGroup title='Zip'>
              <Input
                value={form.values.address?.zip ?? ''}
                onChange={(e) => form.setValue('address.zip', e.target.value)}
                placeholder='Zip'
                disabled={!canEdit}
              />
            </InputGroup>
          </InlineGroup>
        </InlineGroup>

        <div style={{ display: 'flex', width: '100%' }}>
          {!isEditing && (
            <Button
              variant='outline-primary'
              onClick={() => form.submitAndReset(['name'])}
              disabled={!form.valid || form.submitting}
              loading={form.submitting}>
              Save and Add Another
            </Button>
          )}
          <div style={{ flex: 1 }} />
          <ButtonGroup>
            <Button variant='ghost' onClick={form.goBack}>
              Cancel
            </Button>
            <Button onClick={form.submit} disabled={!form.valid || form.submitting} loading={form.submitting}>
              {isEditing ? 'Update Location' : 'Create Location'}
            </Button>
          </ButtonGroup>
        </div>

        {archiveTarget && (
          <StandardModal
            title='Archive Group'
            colorTheme={ColorThemeName.Danger}
            onCancel={() => setArchiveTarget(null)}>
            <ModalContent>
              <BodySmall style={{ fontWeight: 700 }}>Are you sure you want to archive this group?</BodySmall>
            </ModalContent>
            <Actions>
              <Button variant='ghost' onClick={() => setArchiveTarget(null)}>
                Cancel
              </Button>
              <Button variant='danger' onClick={handleArchiveGroup}>
                Archive Group
              </Button>
            </Actions>
          </StandardModal>
        )}
      </>
    </AdminFormPage>
  );
}

const InlineGroup = styled.div`
  display: flex;
  gap: 16px;

  > * {
    flex: 1;
  }
`;

const GroupsTableWrapper = styled.div`
  margin-bottom: 16px;
`;

const IconContainer = styled.div`
  cursor: pointer;
  width: 16px;
`;

const BoldText = styled.span`
  font-weight: 700;
`;

const GroupLink = styled.a`
  color: ${colors.primaryBlue};
  cursor: pointer;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const CenteredSpinner = styled(CircularProgress)`
  margin: 64px auto;
  display: block;
`;
